/**
 * Client-side port of the workspace display-name uniqueness normalizer +
 * validators (workspace-file-aliases plan D3/D5; runtime contract C4).
 *
 * ⚠ VERBATIM BEHAVIORAL PORT of `@lexiun/lx-kernel`'s `display-meta.ts`
 * (`normalizeDisplayName` / `validateAlias` / `validateDescription`). The server
 * (`document.set_display`, P2) is the authoritative validator; this copy exists
 * only for INLINE PRE-SUBMIT validation + sibling-uniqueness checks in the rename
 * popover, so the lawyer gets instant feedback without a round-trip. KEEP IN SYNC
 * — a divergence here just means the client pre-check disagrees with the server,
 * which the server still catches; but they should match. Cross-checked against the
 * lx-kernel source 2026-07-03 (contract-freeze handback C4).
 *
 * The control-char ranges use explicit `\u` escapes (equal to lx-kernel's literal
 * ranges) for hand-edit safety; the combining-mark strip likewise uses
 * `̀-ͯ` (behaviorally identical to lx-kernel's literal U+0300–U+036F
 * range — café ≡ cafe ≡ CAFÉ).
 */

/** Max length of an alias (D5) — mirrors lx-kernel `MAX_ALIAS_LEN`. */
export const MAX_ALIAS_LEN = 120;
/** Max length of a description (D5) — mirrors lx-kernel `MAX_DESCRIPTION_LEN`. */
export const MAX_DESCRIPTION_LEN = 500;

/** Structured validation outcome; `error` is a stable machine code (matches the
 *  server `invalid_display_name` reason + lx-kernel `DisplayValidation.error`). */
export interface DisplayValidation {
  ok: boolean;
  error?: "empty" | "too_long" | "slash" | "control_char" | "leading_dot_or_underscore";
}

/**
 * Case-, accent-, and whitespace-insensitive fold for display-name uniqueness
 * (D3). NFD + combining-mark strip + lowercase + whitespace collapse + trim.
 * Verbatim behavioral port of lx-kernel `normalizeDisplayName`.
 */
export function normalizeDisplayName(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * The effective display name of a node for uniqueness comparison: the alias if
 * set (trimmed), else the raw on-disk name. Mirrors lx-kernel `effectiveDisplayName`.
 */
export function effectiveDisplayName(rawName: string, alias?: string): string {
  return alias && alias.trim() ? alias.trim() : rawName;
}

/**
 * Validate an alias per D5: trimmed non-empty, ≤120 chars, no `/`, no control
 * chars, no leading `.`/`_`. Verbatim port of lx-kernel `validateAlias`. Clearing
 * (null/"") is the caller's concern; this validates a value being SET.
 */
export function validateAlias(alias: string): DisplayValidation {
  const t = alias.trim();
  if (t === "") return { ok: false, error: "empty" };
  if (t.length > MAX_ALIAS_LEN) return { ok: false, error: "too_long" };
  if (t.includes("/")) return { ok: false, error: "slash" };
  if (/[\u0000-\u001f\u007f]/.test(t)) return { ok: false, error: "control_char" };
  if (t.startsWith(".") || t.startsWith("_")) {
    return { ok: false, error: "leading_dot_or_underscore" };
  }
  return { ok: true };
}

/**
 * Validate a description per D5: ≤500 chars, plain text (no control chars except
 * tab/newline). Empty allowed. Verbatim port of lx-kernel `validateDescription`.
 */
export function validateDescription(description: string): DisplayValidation {
  if (description.length > MAX_DESCRIPTION_LEN) return { ok: false, error: "too_long" };
  if (/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(description)) {
    return { ok: false, error: "control_char" };
  }
  return { ok: true };
}

/**
 * True when `candidate`'s effective display name collides (case/accent/whitespace-
 * insensitive) with any SIBLING's effective display name. `siblingEffectiveNames`
 * is the set of effective names already present in the same directory (exclude the
 * node being edited). Returns the colliding sibling's raw effective name, or null
 * if unique. The client pre-check mirrors the server D3 enforcement.
 */
export function findDisplayNameCollision(
  candidate: string,
  siblingEffectiveNames: readonly string[],
): string | null {
  const norm = normalizeDisplayName(candidate);
  if (norm === "") return null;
  for (const sib of siblingEffectiveNames) {
    if (normalizeDisplayName(sib) === norm) return sib;
  }
  return null;
}

// ── @tag reference handle — grammar + validation (workspace-file-tags C3, §7) ──
// VERBATIM behavioral port of lx-kernel `display-meta.ts` (frozen at [tags-M1]
// commit 4007ef98). KEEP IN SYNC — the server (`document.set_display`) is the
// authoritative validator; this copy drives inline pre-submit feedback + the
// tag-first tree. The tag is the case-scope-unique HANDLE; the `title` is the
// citation name (validated as the former alias). Cross-checked against the kernel
// source 2026-07-03.

/** Min / max tag length (grammar bounds, C3) — mirrors lx-kernel. */
export const MIN_TAG_LEN = 3;
export const MAX_TAG_LEN = 40;
/** Hard title cap (A3) — the title reuses the alias rule set + cap. */
export const MAX_TITLE_LEN = MAX_ALIAS_LEN;

/**
 * The tag grammar (C3, analysis §7): the body WITHOUT the leading `@` — a Unicode
 * letter, then letters/digits/underscore, total length 3–40. CamelCase is the
 * authored convention. Verbatim port of lx-kernel `TAG_RE`.
 */
export const TAG_RE = /^\p{L}[\p{L}\p{N}_]{2,39}$/u;

/** Structured tag-validation outcome; `reason` is the stable `invalid_tag` code
 *  (matches the server error + lx-kernel `TagValidation.reason`). */
export interface TagValidation {
  ok: boolean;
  reason?: "empty" | "too_short" | "too_long" | "leading_non_letter" | "bad_char";
}

/**
 * Validate a tag body (a leading `@` is stripped first) per C3/§7 — same check
 * ORDER as lx-kernel `validateTag` so the reason code matches the server.
 */
export function validateTag(tag: string): TagValidation {
  const t = tag.trim().replace(/^@/, "");
  if (t === "") return { ok: false, reason: "empty" };
  if (t.length < MIN_TAG_LEN) return { ok: false, reason: "too_short" };
  if (t.length > MAX_TAG_LEN) return { ok: false, reason: "too_long" };
  if (!/^\p{L}/u.test(t)) return { ok: false, reason: "leading_non_letter" };
  if (!TAG_RE.test(t)) return { ok: false, reason: "bad_char" };
  return { ok: true };
}

/**
 * The normalized REGISTRY KEY for a tag: strip a leading `@`, then the shared
 * casefold + accent-strip fold. `@Contestação` and `@contestacao` share a key.
 * Verbatim port of lx-kernel `normalizeTagKey`.
 */
export function normalizeTagKey(tag: string): string {
  return normalizeDisplayName(tag.replace(/^@/, ""));
}

/**
 * Validate a title (the citation name) — identical rule set to the former alias
 * slot (≤120, no `/`, no control chars, no leading `.`/`_`). Title uniqueness is
 * DROPPED (A16). Verbatim port of lx-kernel `validateTitle`.
 */
export function validateTitle(title: string): DisplayValidation {
  return validateAlias(title);
}
